import {
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  Button,
  Checkbox,
  ListItem,
  ListItemText,
  Autocomplete as MuiAutocomplete,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { useAutocompleteManageProps } from './hooks/useAutocompleteManageProps'
import { useTexts } from './hooks/useTexts'

import { type FormControlLabelProps } from '@mui/material/FormControlLabel'
import { SELECT_OPTION } from './constants'
import { AutocompleteOption, TagsFieldValue } from './types'

import { Footer } from './components/Footer'

export type TagsFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = TagsFieldValue,
> = {
  formControlLabelProps?: Partial<FormControlLabelProps>
  options: AutocompleteOption[]
} & TidalFormBridgeOptions<TagsFieldValue, TargetField, State, AcceptedFormValue>

export function TagsField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = TagsFieldValue,
>(props: TagsFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    label,
    helperText,
    options,
    validateOn = 'change',
    testId,
    disabled = false,
    ...rest
  } = props

  const {
    fieldInfo: { onChange, value, errorOrHelperText, fieldHasError },
  } = useTidalFormBridge<TagsFieldValue, TargetField, State, AcceptedFormValue>({
    validateOn,
    ...rest,
  })

  const defaultProps = {
    options,
    freeSolo: false,
    multiple: true,
    disabled,
    disableCloseOnSelect: true,
    componentsProps: {
      popper: {
        sx: {
          '& .MuiAutocomplete-listbox': {
            padding: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          },
        },
      },
    },
  }

  const texts = useTexts()

  const {
    inputValue,
    selectedOptions,
    isOptionEqualToValue,
    getOptionLabel,
    getOptionDisabled,
    filterOptions,
    onChangeValue,
    onChangeInput,
    onClearInput,
    onSelectDeselectAllOptions,
  } = useAutocompleteManageProps(onChange, value, options)

  return (
    <MuiAutocomplete
      {...defaultProps}
      value={selectedOptions}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
      onChange={onChangeValue}
      inputValue={inputValue}
      onInputChange={onChangeInput}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={filterOptions}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          placeholder={texts.placeholder}
          error={fieldHasError}
          helperText={errorOrHelperText || helperText}
          data-testid={testId}
          fullWidth
        />
      )}
      renderOption={(props, option: AutocompleteOption, state: AutocompleteRenderOptionState) => {
        //Manage select/deselect options and clear search
        if (option.value === SELECT_OPTION.value) {
          const searchValue = inputValue?.trim().toLowerCase() || ''
          //Filter options by input value
          const filteredOptionsBySearchValue = options.filter(option =>
            option.label.toLowerCase().includes(searchValue),
          )
          //Remove disabled options
          const selectableFilteredOptions = filteredOptionsBySearchValue.filter(
            option => !option.disabled,
          )
          //Check if all selectable options are selected
          const allOptionsSelected = selectableFilteredOptions.every(option =>
            selectedOptions.some(selected => selected.value === option.value),
          )
          //Check if there are any selectable options
          const isOptionsAvailable = selectableFilteredOptions.length > 0

          return (
            <>
              {isOptionsAvailable ? (
                <Footer>
                  <Button
                    fullWidth
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                    }}
                    onClick={e => {
                      e.stopPropagation()
                      onSelectDeselectAllOptions(allOptionsSelected, selectableFilteredOptions)
                    }}
                  >
                    {allOptionsSelected
                      ? texts.deselectAllTags(selectableFilteredOptions.length)
                      : texts.selectAllTags(selectableFilteredOptions.length)}
                  </Button>
                </Footer>
              ) : (
                <>
                  <ListItem sx={{ height: '50px', marginRight: '40px' }}>
                    <ListItemText>{texts.emptyTags}</ListItemText>
                  </ListItem>
                  <Footer>
                    <Button
                      fullWidth
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}
                      onClick={e => {
                        e.stopPropagation()
                        onClearInput()
                      }}
                    >
                      {texts.clearSearch}
                    </Button>
                  </Footer>
                </>
              )}
            </>
          )
        }

        return (
          <Tooltip
            key={state.index}
            title={option.disabled ? texts.disabledTooltip : null}
            placement="bottom"
          >
            <span>
              <ListItem {...props} disablePadding disableGutters sx={{ height: '50px' }}>
                <Checkbox checked={state.selected} />
                <ListItemText>{option.label}</ListItemText>
              </ListItem>
            </span>
          </Tooltip>
        )
      }}
    />
  )
}
